/* eslint-disable camelcase */
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Button, notification } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';

import Tooltip from '@totem/components/common/tooltip/Tooltip';
import { sortStringAscending } from '@totem/components/services/threats/eventsContainer_legacy/utilities';
import BuildingMapperContext from '@totem/components/settings/sections/nozomi/buildingMapping/BuildingMapperContext';
import { SensorQueryResultItem } from '@totem/components/settings/sections/nozomi/buildingMapping/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import IBDIcon from '@totem/styles/Icon';
import { isNull } from '@totem/utilities/common';
import { NOZOMI_ENDPOINT } from '@totem/utilities/endpoints';

import '../../../../settings/settings.css';

const styles = {
  iconButton: {
    margin: '0 2px',
  },
};

type Props = {
  buildingId: string;
};

const NozomiBuildingMappingTable = ({ buildingId }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { input, setInput, data, loading, onAction, onActionComplete } =
    useContext(BuildingMapperContext);
  const [isSending, setIsSending] = useState(false);

  const filteredData = useMemo(() => {
    if (isNull(data)) {
      return [];
    }
    return data.filter((item) => item.building.id === buildingId);
  }, [data]);

  const sendSiteDelete = useCallback(
    async (tenantId: string, siteId: string) => {
      if (isSending) {
        return;
      }

      setIsSending(true);

      fetch(`${NOZOMI_ENDPOINT}/sites/${tenantId}/${siteId}`, {
        method: 'DELETE',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => {
          if (res.status < 400) {
            onActionComplete(true);
          } else {
            notification.error({
              title: 'Error site delete!',
              description: 'An error occurred while deleting site.',
              duration: 0,
            });

            onActionComplete(false);
          }
        })
        .then(() => {
          setIsSending(false);
        });
    },
    [isSending],
  );

  const onDelete = (record: SensorQueryResultItem) => {
    sendSiteDelete(record.tenantId, record.siteId);
  };

  const columns: ColumnProps<SensorQueryResultItem>[] = [
    {
      title: 'Site',
      dataIndex: 'siteName',
      key: 'siteName',
      sorter: (compA, compB) =>
        sortStringAscending(compA.siteName, compB.siteName),
    },
    {
      title: 'Host',
      dataIndex: 'host',
      key: 'host',
      render: (text: string, item: SensorQueryResultItem) => {
        return item.sensor.host;
      },
      sorter: (compA, compB) =>
        sortStringAscending(compA.sensor.host, compB.sensor.host),
    },
    {
      title: 'Software',
      dataIndex: 'software',
      key: 'software',
      render: (text: string, item: SensorQueryResultItem) => {
        return item.sensor.software;
      },
      sorter: (compA, compB) =>
        sortStringAscending(compA.sensor.software, compB.sensor.software),
    },
    {
      title: 'Region',
      dataIndex: 'region',
      key: 'region',
      render: (text: string, item: SensorQueryResultItem) => {
        if (typeof item.region !== 'undefined' && item.region !== null) {
          return item.region.name;
        }
        return '-';
      },
      sorter: (compA, compB) =>
        sortStringAscending(
          typeof compA.region !== 'undefined' && compA.region !== null
            ? compA.region.name
            : '-',
          typeof compB.region !== 'undefined' && compB.region !== null
            ? compB.region.name
            : '-',
        ),
    },
    {
      title: 'Building',
      dataIndex: 'building',
      key: 'building',
      render: (text: string, item: SensorQueryResultItem) => {
        if (typeof item.building !== 'undefined' && item.building !== null) {
          return item.building.name;
        }
        return '-';
      },
      sorter: (compA, compB) =>
        sortStringAscending(
          typeof compA.building !== 'undefined' && compA.building !== null
            ? compA.building.name
            : '-',
          typeof compB.building !== 'undefined' && compB.building !== null
            ? compB.building.name
            : '-',
        ),
    },
    {
      title: 'Actions',
      width: 30,
      dataIndex: 'additionalOptions',
      key: 'action',
      render: (_, item: SensorQueryResultItem) => (
        <div className="center-table-cell">
          {permissions.systemAdministrator.hasPermissions(userProfile) && (
            <Tooltip title="Update Building Mapping" placement="top">
              <Button
                icon={<EditOutlined />}
                shape="circle"
                onClick={(): void => onAction('showMappingDialog', item)}
                style={styles.iconButton}
              />
            </Tooltip>
          )}
          {permissions.systemAdministrator.hasPermissions(userProfile) && (
            <Tooltip title="Delete Site" placement="top">
              <Button
                icon={<IBDIcon name={'Delete'} />}
                shape="circle"
                onClick={(): void => onDelete(item)}
                style={styles.iconButton}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const handleTableChange = (
    updatedPagination: TablePaginationConfig,
    filters: SorterResult<SensorQueryResultItem>,
    sorter,
  ) => {
    const { ...params } = filters;

    let sortDir: string = sorter.order === 'descend' ? '-1' : '1';
    if (typeof sorter.order === 'undefined' || sorter.order === null) {
      if (
        typeof input.sensor_sortDirection !== 'undefined' &&
        input.sensor_sortDirection !== null
      ) {
        sortDir = input.sensor_sortDirection;
      }
    }

    let sortField = input.sensor_sortField
      ? input.sensor_sortField
      : 'startTime';
    if (
      typeof sorter.field !== 'undefined' &&
      typeof sorter.order !== 'undefined'
    ) {
      sortField = sorter.field;
    }

    // @ts-ignore
    setInput({
      ...input,
      ...params,
      sensor_pageSize: updatedPagination.pageSize,
      sensor_page: updatedPagination.current,
      sensor_sortField: sortField,
      sensor_sortDirection: sortDir,
    });
  };

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={filteredData}
      loading={loading}
      onChange={handleTableChange}
      pagination={{
        current: input.sensor_page,
        pageSize: input.sensor_pageSize,
        total: filteredData.length,
        showSizeChanger: true,
      }}
      rowKey={(record) => record.sensor.id}
    />
  );
};

export default NozomiBuildingMappingTable;
